import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { withAllContexts } from '../../AllContextsWrapper';
import { useSnackbar } from 'notistack';
import { ArrowLeft, Save, Copy, Check, Info } from 'lucide-react';

const AdminPluginPage = (props) => {
    const { id: calendarId, plugin: pluginName } = useParams();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [pluginData, setPluginData] = useState(null);
    const [settings, setSettings] = useState({});
    const [activeTab, setActiveTab] = useState('sk'); // 'sk' or 'en'
    const [selectedEvent, setSelectedEvent] = useState(null); // Currently selected event

    // Generic Reservation Variables
    // Fetched from backend
    const [reservationVars, setReservationVars] = useState([]);

    const getVariablesForEvent = (eventId) => {
        const defaults = reservationVars;

        if (!eventId) return defaults;

        if (eventId.startsWith('payment')) {
            return [
                { name: 'payment_amount', desc: 'Suma platby' },
                { name: 'payment_type', desc: 'Typ platby' },
                { name: 'payment_date', desc: 'Dátum platby' },
                { name: 'payment_note', desc: 'Poznámka platby' },
                ...defaults
            ];
        }

        if (eventId.startsWith('client')) {
            return [
                { name: 'name', desc: 'Meno' },
                { name: 'email', desc: 'Email' },
                { name: 'phone', desc: 'Telefón' },
                { name: 'note', desc: 'Poznámka' },
            ];
        }

        if (eventId.startsWith('giftcard')) {
            return [
                { name: 'code', desc: 'Kód poukážky' },
                { name: 'balance', desc: 'Zostatok' },
            ];
        }

        return defaults;
    };

    const availableHooks = [
        { id: 'reservation.created', label: 'Vytvorenie rezervácie' },
        { id: 'reservation.updated', label: 'Úprava rezervácie' },
        { id: 'reservation.deleted', label: 'Zmazanie rezervácie' },

        // Granular Status Changes
        { id: 'reservation.status_change.ordered', label: 'Stav: Objednaná (Ordered)' },
        { id: 'reservation.status_change.onwater', label: 'Stav: Na vode (On Water)' },
        { id: 'reservation.status_change.finished', label: 'Stav: Ukončená (Finished)' },
        { id: 'reservation.status_change.canceled', label: 'Stav: Zrušená (Canceled)' },

        { id: 'reservation.reminder', label: 'Pripomienka rezervácie' },
        { id: 'payment.created', label: 'Prijatie platby' },
        { id: 'payment.deleted', label: 'Zmazanie platby' },
        { id: 'client.created', label: 'Vytvorenie klienta' },
        { id: 'client.updated', label: 'Úprava klienta' },
        { id: 'calendar.created', label: 'Vytvorenie kalendára' },
        { id: 'fee.created', label: 'Pridanie poplatku' },
        { id: 'giftcard.redeemed', label: 'Uplatnenie darčeka' },
    ];

    useEffect(() => {
        if (props.socketIO && props.socketIO.socket) {
            fetchPluginSettings();
            fetchVariables();
        }
    }, [calendarId, pluginName, props.socketIO]);

    const fetchVariables = () => {
        props.socketIO.socket.emit('calendars:getVariables', parseInt(calendarId), (response) => {
            if (response.success) {
                setReservationVars(response.data);
            }
        });
    };

    const fetchPluginSettings = () => {
        setIsLoading(true);
        props.socketIO.socket.emit('calendars:getPlugins', parseInt(calendarId), (response) => {
            if (response.success) {
                const plugin = response.data.find(p => p.plugin_name === pluginName);
                if (plugin) {
                    setPluginData(plugin);
                    let currentSettings = plugin.settings || {};
                    if (!currentSettings.events) currentSettings.events = {};
                    setSettings(currentSettings);

                    // Select first active event or first available
                    const firstActive = availableHooks.find(h => currentSettings.events[h.id]?.enabled);
                    setSelectedEvent(firstActive ? firstActive.id : availableHooks[0].id);
                } else {
                    enqueueSnackbar("Plugin nenájdený", { variant: 'error' });
                }
            } else {
                enqueueSnackbar("Nepodarilo sa načítať nastavenia pluginu", { variant: 'error' });
            }
            setIsLoading(false);
        });
    };

    const handleSave = () => {
        if (!pluginData) return;

        props.socketIO.socket.emit('calendars:updatePlugin', {
            calendarId: parseInt(calendarId),
            pluginName: pluginName,
            enabled: pluginData.enabled,
            settings: settings
        }, (response) => {
            if (response.success) {
                enqueueSnackbar("Nastavenia boli uložené", { variant: 'success' });
            } else {
                enqueueSnackbar("Chyba pri ukladaní", { variant: 'error' });
            }
        });
    };

    const handleEventToggle = (eventName) => {
        const newSettings = { ...settings };
        if (!newSettings.events[eventName]) newSettings.events[eventName] = {};
        newSettings.events[eventName].enabled = !newSettings.events[eventName].enabled;
        setSettings(newSettings);
    };

    const handleTemplateChange = (eventName, value) => {
        const newSettings = { ...settings };
        if (!newSettings.events[eventName]) newSettings.events[eventName] = {};
        if (!newSettings.events[eventName].templates) newSettings.events[eventName].templates = {};

        newSettings.events[eventName].templates[activeTab] = value;
        setSettings(newSettings);
    };

    const getTemplateValue = (eventName) => {
        const eventConfig = settings.events?.[eventName] || {};
        if (eventConfig.templates && eventConfig.templates[activeTab]) {
            return eventConfig.templates[activeTab];
        }
        if (activeTab === 'sk' && eventConfig.template) {
            return eventConfig.template;
        }
        return "";
    };

    const handleInsertVariable = (variableName) => {
        const insertText = `{${variableName}}`;
        const currentVal = getTemplateValue(selectedEvent);
        handleTemplateChange(selectedEvent, currentVal + insertText);
    };

    // Sort hooks: Active first, then by defined order
    const sortedHooks = [...availableHooks].sort((a, b) => {
        const aEnabled = settings.events?.[a.id]?.enabled;
        const bEnabled = settings.events?.[b.id]?.enabled;
        if (aEnabled && !bEnabled) return -1;
        if (!aEnabled && bEnabled) return 1;
        return 0; // Default order
    });

    if (isLoading) return <div className="p-8 text-center text-gray-500">Načítavam...</div>;

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center justify-between sticky top-0 z-20 shadow-sm gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => history.push(`/admin/calendar/${calendarId}/plugins`)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <div className="text-sm text-gray-500">Administrácia / Pluginy / SMS</div>
                        <div className="text-xl font-bold text-gray-900 truncate max-w-[200px] md:max-w-none">
                            Nastavenie SMS
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    className="w-full md:w-auto justify-center px-4 py-3 md:py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
                >
                    <Save size={18} />
                    Uložiť zmeny
                </button>
            </div>

            <div className="max-w-7xl mx-auto mt-4 md:mt-8 px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">

                {/* Left Column: Hooks List */}
                <div className="col-span-1 lg:col-span-3 order-2 lg:order-1">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-24 md:top-32">

                        {/* Global Settings Section */}
                        <div className="border-b border-gray-200">
                            <div className="p-4 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">
                                Nastavenia
                            </div>
                            <button
                                onClick={() => setSelectedEvent('settings.general')}
                                className={`w-full text-left p-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${selectedEvent === 'settings.general' ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
                            >
                                <div>
                                    <div className={`text-sm font-medium ${selectedEvent === 'settings.general' ? 'text-blue-700' : 'text-gray-900'}`}>
                                        Všeobecné nastavenia
                                    </div>
                                    <div className="text-xs text-gray-400 font-mono mt-0.5">Sender ID</div>
                                </div>
                            </button>
                        </div>

                        <div className="p-4 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">
                            Udalosti (Hooks)
                        </div>
                        <div className="divide-y divide-gray-100">
                            {sortedHooks.map(hook => {
                                const isEnabled = settings.events?.[hook.id]?.enabled;
                                const isSelected = selectedEvent === hook.id;

                                return (
                                    <button
                                        key={hook.id}
                                        onClick={() => setSelectedEvent(hook.id)}
                                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
                                    >
                                        <div>
                                            <div className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>{hook.label}</div>
                                            <div className="text-xs text-gray-400 font-mono mt-0.5">{hook.id}</div>
                                        </div>
                                        {isEnabled && <div className="w-2 h-2 rounded-full bg-green-500"></div>}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Column: Editor */}
                <div className="col-span-1 lg:col-span-9 order-1 lg:order-2">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                        {selectedEvent === 'settings.general' ? (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">
                                            Všeobecné nastavenia
                                        </h2>
                                        <p className="text-sm text-gray-500 font-mono">settings.general</p>
                                    </div>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="text-sm font-bold text-gray-900 mb-2">Všeobecné nastavenia SMS</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Tieto nastavenia sa aplikujú na všetky odosielané správy z tohto kalendára.
                                    </p>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Meno odosielateľa (Sender ID)
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                maxLength={11}
                                                value={settings.sender_id || ''}
                                                onChange={(e) => {
                                                    setSettings({ ...settings, sender_id: e.target.value });
                                                }}
                                                placeholder="Napr. Splavma"
                                                className="block w-full max-w-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Maximálne 11 znakov (a-Z, 0-9). Musí byť povolené operátorom.
                                            Ak necháte prázdne, použije sa predvolené číslo/názov.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">
                                            {availableHooks.find(h => h.id === selectedEvent)?.label}
                                        </h2>
                                        <p className="text-sm text-gray-500 font-mono">{selectedEvent}</p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-medium ${settings.events?.[selectedEvent]?.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                            {settings.events?.[selectedEvent]?.enabled ? 'Aktívne' : 'Neaktívne'}
                                        </span>
                                        <button
                                            onClick={() => handleEventToggle(selectedEvent)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${settings.events?.[selectedEvent]?.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.events?.[selectedEvent]?.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                                            />
                                        </button>
                                    </div>
                                </div>

                                {/* Trigger Fields Configuration for Reservation Updated */}
                                {selectedEvent === 'reservation.updated' && (
                                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                            Spúšťače aktualizácie
                                        </h3>
                                        <p className="text-xs text-gray-500 mb-3">
                                            Vyberte, ktoré zmeny v rezervácii majú spustiť odoslanie SMS. Ak nevyberiete nič, SMS sa odošle pri každej uloženej zmene.
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-2">
                                            {/* Prepend Core Fields if not in variables, or just use variables if they cover everything. 
                                                User said "uplne vsetky polia aktualneho kalendara, presne tak ako sa vypisuju tie premenne".
                                                So we use reservationVars. 
                                            */}
                                            {reservationVars.map(field => {
                                                const triggers = settings.events?.[selectedEvent]?.trigger_fields || [];
                                                const isChecked = triggers.includes(field.name);
                                                return (
                                                    <label key={field.name} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-100 transition-colors">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                const newSettings = { ...settings };
                                                                if (!newSettings.events[selectedEvent]) newSettings.events[selectedEvent] = {};
                                                                let currentTriggers = newSettings.events[selectedEvent].trigger_fields || [];

                                                                if (e.target.checked) {
                                                                    currentTriggers = [...currentTriggers, field.name];
                                                                } else {
                                                                    currentTriggers = currentTriggers.filter(t => t !== field.name);
                                                                }

                                                                newSettings.events[selectedEvent].trigger_fields = currentTriggers;
                                                                setSettings(newSettings);
                                                            }}
                                                            className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4 border-gray-300"
                                                        />
                                                        <span className="text-sm text-gray-700">{field.desc || field.name} <span className="text-xs text-gray-400 font-mono">({field.name})</span></span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Timing Settings for Reminder */}
                                {selectedEvent === 'reservation.reminder' && (
                                    <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                                            Načasovanie pripomienky
                                        </h3>
                                        <div className="space-y-4">
                                            {/* Offset Configuration */}
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={settings.events?.[selectedEvent]?.schedule?.value || 1}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value) || 1;
                                                            const newSettings = { ...settings };
                                                            if (!newSettings.events[selectedEvent].schedule) newSettings.events[selectedEvent].schedule = { unit: 'days' };
                                                            newSettings.events[selectedEvent].schedule.value = val;
                                                            setSettings(newSettings);
                                                        }}
                                                        className="w-20 p-2 text-sm border-none focus:ring-0 rounded-l-md text-right"
                                                    />
                                                    <div className="border-l border-gray-300 h-6"></div>
                                                    <select
                                                        value={settings.events?.[selectedEvent]?.schedule?.unit || 'days'}
                                                        onChange={(e) => {
                                                            const newSettings = { ...settings };
                                                            if (!newSettings.events[selectedEvent].schedule) newSettings.events[selectedEvent].schedule = { value: 1 };
                                                            newSettings.events[selectedEvent].schedule.unit = e.target.value;
                                                            setSettings(newSettings);
                                                        }}
                                                        className="p-2 text-sm border-none focus:ring-0 bg-transparent rounded-r-md text-gray-700 font-medium cursor-pointer"
                                                    >
                                                        <option value="days">dní</option>
                                                        <option value="hours">hodín</option>
                                                    </select>
                                                </div>
                                                <span className="text-sm text-gray-600 font-medium">pred začiatkom rezervácie</span>
                                            </div>

                                            {/* Execution Time Configuration (Only for Days) */}
                                            {(!settings.events?.[selectedEvent]?.schedule?.unit || settings.events?.[selectedEvent]?.schedule?.unit === 'days') && (
                                                <div className="pt-2 border-t border-yellow-200">
                                                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                                        Čas kontroly (Denný Cron)
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="time"
                                                            value={settings.events?.[selectedEvent]?.schedule?.time || "09:00"}
                                                            onChange={(e) => {
                                                                const newSettings = { ...settings };
                                                                if (!newSettings.events[selectedEvent].schedule) newSettings.events[selectedEvent].schedule = { value: 1, unit: 'days' };
                                                                newSettings.events[selectedEvent].schedule.time = e.target.value;
                                                                setSettings(newSettings);
                                                            }}
                                                            className="p-2 border border-gray-300 rounded-md text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                                        />
                                                        <span className="text-xs text-gray-500">
                                                            Správa sa odošle v tento čas (ak server beží).
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            <p className="text-xs text-gray-500 mt-2">
                                                Správa bude odoslaná {settings.events?.[selectedEvent]?.schedule?.value || 1} {(settings.events?.[selectedEvent]?.schedule?.unit === 'hours' ? 'hodín' : 'dní')} pred termínom.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Variables Toolbar */}
                                <div className="mb-4">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dostupné premenné</div>
                                    <div className="flex flex-wrap gap-2">
                                        {getVariablesForEvent(selectedEvent).map(v => (
                                            <button
                                                key={v.name}
                                                onClick={() => handleInsertVariable(v.name)}
                                                className="inline-flex items-center px-2 py-1 rounded bg-gray-100 border border-gray-200 text-xs font-mono text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors"
                                                title={v.desc}
                                            >
                                                {`{${v.name}}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Language Tabs */}
                                <div className="border-b border-gray-200 mb-4">
                                    <nav className="-mb-px flex space-x-8">
                                        {[
                                            { id: 'sk', label: 'Slovenčina' },
                                            { id: 'en', label: 'English' }
                                        ].map(lang => (
                                            <button
                                                key={lang.id}
                                                onClick={() => setActiveTab(lang.id)}
                                                className={`
                                                    whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                                                    ${activeTab === lang.id
                                                        ? 'border-blue-500 text-blue-600'
                                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                                                `}
                                            >
                                                {lang.label}
                                            </button>
                                        ))}
                                    </nav>
                                </div>

                                {/* Editor */}
                                <div className="space-y-4">
                                    <div className="relative">
                                        <textarea
                                            disabled={!settings.events?.[selectedEvent]?.enabled}
                                            value={getTemplateValue(selectedEvent)}
                                            onChange={(e) => handleTemplateChange(selectedEvent, e.target.value)}
                                            className="block w-full rounded-lg border-gray-300 border shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 font-mono text-sm p-4 min-h-[300px]"
                                            placeholder={settings.events?.[selectedEvent]?.enabled ? "Text SMS správy..." : "Pre úpravu textu najprv aktivujte túto udalosť."}
                                        />
                                        {!settings.events?.[selectedEvent]?.enabled && (
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-gray-200 shadow-sm text-gray-500 font-medium">
                                                    Udalosť je neaktívna
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-start gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                        <Info size={16} className="mt-0.5 text-blue-500 flex-shrink-0" />
                                        <p>
                                            SMS správa bude odoslaná automaticky pri nastatí udalosti <strong>{availableHooks.find(h => h.id === selectedEvent)?.label}</strong>.
                                            Uistite sa, že text správy neobsahuje diakritiku pre maximálnu kompatibilitu, hoci systém sa ju pokúsi odstrániť automaticky.
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default withAllContexts(AdminPluginPage);
